import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, Send, Loader, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Query, ID } from "appwrite";
import { useStateValue } from "../../StateProvider";
import { tablesDB, DB_ID, CHATS_TABLE, MESSAGES_TABLE } from "../../appwrite";
import "./MobileWindow.css";
import tbLogo from "../Assets/images/tb_logo.png";

const ADMIN_SENDER = "support@trendbayds.com";
const POLL_MS = 2000;

export default function MobileWindow() {
  const [{ user }] = useStateValue();
  const navigate = useNavigate();
  const [chatId, setChatId]     = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState("");
  const [loading, setLoading]   = useState(false);
  const [sending, setSending]   = useState(false);
  const bottomRef  = useRef();
  const pollRef    = useRef(null);
  const pendingIds = useRef(new Set());

  // Get or create chat
  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: CHATS_TABLE,
          queries: [Query.equal("userEmail", user.email), Query.limit(1)],
        });
        const existing = (res.rows || [])[0];
        let id;
        if (existing) {
          id = existing.$id;
        } else {
          const created = await tablesDB.createRow({
            databaseId: DB_ID,
            tableId: CHATS_TABLE,
            rowId: ID.unique(),
            data: {
              userEmail: user.email,
              participants: [user.email, ADMIN_SENDER],
              lastMessageAt: new Date().toISOString(),
            },
          });
          id = created.$id;
        }
        if (!cancelled) setChatId(id);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();

    return () => { cancelled = true; };
  }, [user?.email]);

  // Poll for messages every 2s
  useEffect(() => {
    if (!chatId) return;

    const fetchMessages = async () => {
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: MESSAGES_TABLE,
          queries: [Query.equal("chats", chatId), Query.orderAsc("$createdAt"), Query.limit(200)],
        });
        const serverMsgs = res.rows || [];
        setMessages((prev) => {
          const serverIds = new Set(serverMsgs.map((m) => m.$id));
          const optimistic = prev.filter((m) => pendingIds.current.has(m.$id) && !serverIds.has(m.$id));
          return [...serverMsgs, ...optimistic];
        });
      } catch (e) { console.error(e); }
    };

    fetchMessages();
    pollRef.current = setInterval(fetchMessages, POLL_MS);

    return () => { clearInterval(pollRef.current); pollRef.current = null; };
  }, [chatId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = async () => {
    if (!text.trim() || !chatId || sending) return;
    const body = text.trim();
    const rowId = ID.unique();
    setText("");
    setSending(true);
    pendingIds.current.add(rowId);
    setMessages((prev) => [...prev, {
      $id: rowId, text: body, sender: user.email, chats: chatId,
      $createdAt: new Date().toISOString(),
    }]);
    try {
      await tablesDB.createRow({
        databaseId: DB_ID,
        tableId: MESSAGES_TABLE,
        rowId,
        data: { text: body, sender: user.email, chats: chatId },
      });
      await tablesDB.updateRow({
        databaseId: DB_ID,
        tableId: CHATS_TABLE,
        rowId: chatId,
        data: { lastMessageAt: new Date().toISOString() },
      });
      pendingIds.current.delete(rowId);
    } catch (e) {
      console.error(e);
      pendingIds.current.delete(rowId);
      setMessages((prev) => prev.filter((m) => m.$id !== rowId));
    }
    finally { setSending(false); }
  };

  const isMe = (msg) => msg.sender !== ADMIN_SENDER;

  return (
    <div className="mw-page">
      <div className="mw-header">
        <button className="mw-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <div className="mw-header-info">
          <div className="mw-avatar">
            <img src={tbLogo} alt="TB" />
            <span className="mw-online-dot" />
          </div>
          <div>
            <p className="mw-brand">TrendBay Support</p>
            <p className="mw-status"><span className="mw-status-dot" />Online</p>
          </div>
        </div>
      </div>

      <div className="mw-body">
        {!user ? (
          <div className="mw-state">
            <div className="mw-state-icon"><MessageCircle size={40} color="#00A855" /></div>
            <p className="mw-state-title">Chat with us</p>
            <p className="mw-state-sub">Log in to start a conversation with our support team.</p>
            <a href="/login" className="mw-btn">Log in to chat</a>
          </div>
        ) : loading ? (
          <div className="mw-state">
            <Loader size={32} color="#00A855" className="mw-spinner" />
            <p className="mw-state-sub">Connecting…</p>
          </div>
        ) : (
          <>
            <div className="mw-messages">
              {messages.length === 0 && (
                <div className="mw-welcome">
                  <div className="mw-welcome-avatar"><img src={tbLogo} alt="TB" /></div>
                  <p className="mw-welcome-title">Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""}! 👋</p>
                  <p className="mw-welcome-sub">How can we help you today?</p>
                </div>
              )}
              {messages.map((msg) => (
                <div key={msg.$id} className={`mw-msg ${isMe(msg) ? "mw-msg--me" : "mw-msg--them"}`}>
                  <div className="mw-bubble">{msg.text}</div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="mw-input-bar">
              <input
                className="mw-input"
                type="text"
                placeholder="Type a message…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                disabled={sending}
              />
              <button className="mw-send" onClick={sendMessage} disabled={!text.trim() || sending}>
                {sending ? <Loader size={16} className="mw-spinner" /> : <Send size={16} />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
